import { useEffect, useRef, useState } from "react";
import { BookOpen, ChevronDown, FileText, Wallet, PiggyBank, TrendingUp, Compass, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dropdown in the Admin header listing every role-specific operations manual.
 * Each link is gated by the corresponding view_* permission via useAuth().hasPerm():
 *  - Admin guide       → view_dashboard (everyone gets it as the catch-all)
 *  - Finance guide     → view_finance
 *  - Payroll guide     → view_payroll
 *  - Sales guide       → view_sales
 *  - Clinic guide      → always (public, useful for handing to onboarded clinics)
 */
export default function ManualsMenu() {
    const { hasPerm, isSuperadmin } = useAuth();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // Cookie-only auth (Phase B): same-origin downloads send `tm_auth`
    // automatically — `?token=` is gone.
    const link = (path) => `${API}${path}`;

    const items = [
        { key: "blueprint", label: "Master blueprint", icon: Compass,   href: link("/docs/blueprint"),     gate: () => isSuperadmin,              desc: "Full system reference · architecture · screenshots" },
        { key: "quick-start", label: "Quick start", icon: Sparkles,   href: link("/docs/quick-start"),  gate: () => true,                      desc: "15-min onboarding for new labs" },
        { key: "admin",   label: "Admin guide",   icon: FileText,  href: link("/docs/admin-guide"),   gate: () => hasPerm("view_dashboard"), desc: "Every tab, every screen, every automation" },
        { key: "finance", label: "Finance guide", icon: Wallet,    href: link("/docs/finance-guide"), gate: () => hasPerm("view_finance"),   desc: "Money & books playbook" },
        { key: "payroll", label: "Payroll guide", icon: PiggyBank, href: link("/docs/payroll-guide"), gate: () => hasPerm("view_payroll"),   desc: "Compensation operations" },
        { key: "sales",   label: "Sales guide",   icon: TrendingUp,href: link("/docs/sales-guide"),   gate: () => hasPerm("view_sales"),     desc: "Pipeline · targets · incentives" },
        { key: "clinic",  label: "Clinic guide",  icon: BookOpen,  href: `${API}/docs/clinic-guide`,  gate: () => true,                      desc: "Hand to onboarded clinics" },
    ].filter((it) => it.gate());

    return (
        <div ref={ref} className="relative" data-testid="manuals-menu">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="manuals-toggle"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-50 border border-cyan-200 text-cyan-800 hover:bg-cyan-100 transition-colors text-[12.5px] font-medium"
            >
                <BookOpen size={13} /> Manuals
                <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div
                    className="
                        fixed sm:absolute z-50
                        top-[64px] sm:top-auto sm:mt-2
                        inset-x-3 sm:inset-x-auto sm:end-0
                        sm:w-72
                        rounded-xl bg-white border border-slate-200 shadow-2xl p-2
                        max-h-[calc(100vh-80px)] overflow-y-auto
                    "
                    data-testid="manuals-menu-panel"
                >
                    <div className="px-2 pt-1.5 pb-1 font-mono-tr text-[10px] tracking-[0.22em] uppercase text-slate-500">
                        Role-specific manuals
                    </div>
                    <ul className="mt-1 space-y-0.5">
                        {items.map((it) => {
                            const Icon = it.icon;
                            return (
                                <li key={it.key}>
                                    <a
                                        href={it.href}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`manual-link-${it.key}`}
                                        onClick={() => setOpen(false)}
                                        className="flex items-start gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition"
                                    >
                                        <div className="w-7 h-7 rounded-md grid place-items-center bg-cyan-50 text-cyan-700 shrink-0">
                                            <Icon size={13} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-medium text-slate-900">{it.label}</div>
                                            <div className="text-[11.5px] text-slate-500 truncate">{it.desc}</div>
                                        </div>
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}
